import { useState, useMemo } from 'react';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import { createPathComponent } from '@react-leaflet/core';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import 'leaflet.markercluster';
import 'leaflet.markercluster/dist/MarkerCluster.css';
import 'leaflet.markercluster/dist/MarkerCluster.Default.css';
import { Link } from 'react-router-dom';
import type { ProjectSummary, AskLocation } from '@spi/shared';
import { OrgIcon, AskIcon } from '../shared/EntityIcons';

// React wrapper for Leaflet MarkerClusterGroup
const MarkerClusterGroup = createPathComponent(
  ({ children: _c, ...props }: any, ctx: any) => {
    const clusterGroup = L.markerClusterGroup(props);
    return {
      instance: clusterGroup,
      context: { ...ctx, layerContainer: clusterGroup },
    };
  },
  (instance: any, props: any, prevProps: any) => {
    if (props.iconCreateFunction !== prevProps.iconCreateFunction) {
      (instance as any).options.iconCreateFunction = props.iconCreateFunction;
    }
  },
);

function createClusterIcon(cluster: L.MarkerCluster) {
  const count = cluster.getChildCount();
  const size = count < 10 ? 36 : count < 100 ? 42 : 48;
  return L.divIcon({
    html: `<div style="
      width: ${size}px;
      height: ${size}px;
      display: flex;
      align-items: center;
      justify-content: center;
      background: #059669;
      color: white;
      border-radius: 50%;
      font-size: ${count < 100 ? 14 : 12}px;
      font-weight: 600;
      box-shadow: 0 2px 6px rgba(0,0,0,0.3);
      border: 2px solid white;
    ">${count}</div>`,
    className: '',
    iconSize: L.point(size, size),
  });
}

const greenIcon = new L.Icon({
  iconUrl: 'data:image/svg+xml,' + encodeURIComponent(`
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 36" width="24" height="36">
      <path d="M12 0C5.4 0 0 5.4 0 12c0 9 12 24 12 24s12-15 12-24C24 5.4 18.6 0 12 0z" fill="#059669"/>
      <circle cx="12" cy="12" r="6" fill="white"/>
    </svg>
  `),
  iconSize: [24, 36],
  iconAnchor: [12, 36],
  popupAnchor: [0, -36],
});

function createCountIcon(count: number) {
  return L.divIcon({
    html: `<div style="position:relative;width:24px;height:36px;">
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 36" width="24" height="36">
        <path d="M12 0C5.4 0 0 5.4 0 12c0 9 12 24 12 24s12-15 12-24C24 5.4 18.6 0 12 0z" fill="#059669"/>
        <circle cx="12" cy="12" r="6" fill="white"/>
      </svg>
      <div style="
        position:absolute;
        top:-6px;
        right:-8px;
        min-width:18px;
        height:18px;
        display:flex;
        align-items:center;
        justify-content:center;
        background:#dc2626;
        color:white;
        border-radius:9px;
        font-size:11px;
        font-weight:700;
        padding:0 4px;
        border:1.5px solid white;
        line-height:1;
      ">${count}</div>
    </div>`,
    className: '',
    iconSize: L.point(24, 36),
    iconAnchor: L.point(12, 36),
    popupAnchor: L.point(0, -36),
  });
}

const amberIcon = new L.Icon({
  iconUrl: 'data:image/svg+xml,' + encodeURIComponent(`
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 30" width="20" height="30">
      <path d="M10 0C4.5 0 0 4.5 0 10c0 7.5 10 20 10 20s10-12.5 10-20C20 4.5 15.5 0 10 0z" fill="#d97706"/>
      <circle cx="10" cy="10" r="5" fill="white"/>
    </svg>
  `),
  iconSize: [20, 30],
  iconAnchor: [10, 30],
  popupAnchor: [0, -30],
});

function createAmberClusterIcon(cluster: L.MarkerCluster) {
  const count = cluster.getChildCount();
  const size = count < 10 ? 32 : count < 100 ? 38 : 44;
  return L.divIcon({
    html: `<div style="
      width: ${size}px;
      height: ${size}px;
      display: flex;
      align-items: center;
      justify-content: center;
      background: #d97706;
      color: white;
      border-radius: 50%;
      font-size: ${count < 100 ? 12 : 10}px;
      font-weight: 600;
      box-shadow: 0 2px 6px rgba(0,0,0,0.3);
      border: 2px solid white;
    ">${count}</div>`,
    className: '',
    iconSize: L.point(size, size),
  });
}

// Compute a recent NDVI 8-day composite date (~16 days behind to ensure availability)
function getNdviDate(): string {
  const d = new Date();
  d.setDate(d.getDate() - 16);
  const year = d.getFullYear();
  const startOfYear = new Date(year, 0, 1);
  const dayOfYear = Math.floor((d.getTime() - startOfYear.getTime()) / 86400000) + 1;
  const periodStart = Math.floor((dayOfYear - 1) / 8) * 8 + 1;
  const result = new Date(year, 0, periodStart);
  return result.toISOString().split('T')[0];
}

interface Props {
  projects: ProjectSummary[];
}

interface MarkerGroup {
  key: string;
  lat: number;
  lng: number;
  orgId: number;
  orgName: string;
  country: string;
  zone: string | null;
  projects: ProjectSummary[];
}

export function ProjectsMapView({ projects }: Props) {
  const [showNdvi, setShowNdvi] = useState(false);
  const ndviDate = useMemo(() => getNdviDate(), []);

  const groups = useMemo(() => {
    const map = new Map<string, MarkerGroup>();
    for (const p of projects) {
      if (p.latitude == null || p.longitude == null) continue;
      const key = `${p.latitude},${p.longitude}`;
      const existing = map.get(key);
      if (existing) {
        existing.projects.push(p);
      } else {
        map.set(key, {
          key,
          lat: p.latitude,
          lng: p.longitude,
          orgId: p.orgId,
          orgName: p.orgName,
          country: p.country,
          zone: p.zone,
          projects: [p],
        });
      }
    }
    return Array.from(map.values());
  }, [projects]);

  const askLocations = useMemo(() => {
    const locs: AskLocation[] = [];
    for (const p of projects) {
      if (p.askLocations) locs.push(...p.askLocations);
    }
    return locs;
  }, [projects]);

  return (
    <>
      <div className="flex items-center gap-2 mb-3">
        <label className="flex items-center gap-1.5 text-sm text-spi-slate cursor-pointer select-none">
          <input
            type="checkbox"
            checked={showNdvi}
            onChange={(e) => setShowNdvi(e.target.checked)}
            className="rounded border-gray-300 text-spi-green focus:ring-spi-green"
          />
          Vegetation Index (NDVI)
        </label>
      </div>
      <div className="card overflow-hidden" style={{ height: '600px' }}>
        <MapContainer
          center={[0, 25]}
          zoom={3}
          style={{ height: '100%', width: '100%' }}
          scrollWheelZoom={true}
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> &copy; <a href="https://carto.com/attributions">CARTO</a>'
            url="https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}.png"
          />
          {showNdvi && (
            <TileLayer
              url={`https://gibs.earthdata.nasa.gov/wmts/epsg3857/best/MODIS_Terra_NDVI_8Day/default/${ndviDate}/GoogleMapsCompatible_Level9/{z}/{y}/{x}.png`}
              attribution='NDVI: <a href="https://earthdata.nasa.gov">NASA GIBS</a>'
              opacity={0.55}
              maxNativeZoom={9}
            />
          )}
          <MarkerClusterGroup
            maxClusterRadius={50}
            spiderfyOnMaxZoom={true}
            showCoverageOnHover={false}
            iconCreateFunction={createClusterIcon}
          >
            {groups.map((group) => (
              <Marker
                key={group.key}
                position={[group.lat, group.lng]}
                icon={group.projects.length > 1 ? createCountIcon(group.projects.length) : greenIcon}
              >
                <Popup>
                  <div className="min-w-[200px]">
                    <Link to={`/marketplace/organizations/${group.orgId}`} className="font-semibold text-sm text-spi-green hover:underline inline-flex items-center gap-1">
                      <OrgIcon className="w-3.5 h-3.5" />{group.orgName}
                    </Link>
                    <p className="text-xs text-gray-500">
                      {group.country}{group.zone ? ` \u00b7 Zone ${group.zone}` : ''}
                    </p>
                    <ul className="mt-1.5 space-y-1.5">
                      {group.projects.map((p) => (
                        <li key={p.id}>
                          <Link
                            to={`/marketplace/projects/${p.id}`}
                            className="flex items-center gap-2 text-xs text-spi-green hover:underline font-medium"
                          >
                            {p.thumbnailUrl && (
                              <img
                                src={p.thumbnailUrl}
                                alt=""
                                className="w-10 h-10 rounded object-cover flex-shrink-0"
                              />
                            )}
                            <span>{p.title}</span>
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </div>
                </Popup>
              </Marker>
            ))}
          </MarkerClusterGroup>
          {askLocations.length > 0 && (
            <MarkerClusterGroup
              maxClusterRadius={40}
              spiderfyOnMaxZoom={true}
              showCoverageOnHover={false}
              iconCreateFunction={createAmberClusterIcon}
            >
              {askLocations.map((loc) => (
                <Marker
                  key={`ask-${loc.askId}`}
                  position={[loc.latitude, loc.longitude]}
                  icon={amberIcon}
                >
                  <Popup>
                    <div className="min-w-[160px]">
                      <p className="font-semibold text-sm flex items-center gap-1">
                        <AskIcon className="w-3.5 h-3.5 text-amber-600" />
                        {loc.crop}
                      </p>
                      <Link
                        to={`/marketplace/projects/${loc.projectId}`}
                        className="text-xs text-spi-green hover:underline font-medium mt-1 block"
                      >
                        {loc.projectTitle} &rarr;
                      </Link>
                    </div>
                  </Popup>
                </Marker>
              ))}
            </MarkerClusterGroup>
          )}
        </MapContainer>
      </div>
      {groups.length === 0 && (
        <p className="text-center text-sm text-spi-slate mt-3">No matching projects have organization coordinates set.</p>
      )}
    </>
  );
}
